/**
 * WarehouseContext - Global state for selected warehouse
 * Provides warehouse selection across the entire app
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

export interface Warehouse {
  id: number;
  name: string;
  city: string;
  address: string;
  mapWidth: number;
  mapHeight: number;
  mapBackground?: string;
  startPointX?: number | null;
  startPointY?: number | null;
  startPointName?: string;
  isActive: boolean;
}

// API response format (snake_case)
interface WarehouseApiResponse {
  id: number;
  name: string;
  city: string;
  address: string;
  map_width: number;
  map_height: number;
  map_background?: string;
  start_point_x?: number | null;
  start_point_y?: number | null;
  start_point_name?: string;
  is_active: boolean;
}

// Convert API response to frontend format
const mapWarehouseFromApi = (data: WarehouseApiResponse): Warehouse => ({
  id: data.id,
  name: data.name,
  city: data.city,
  address: data.address,
  mapWidth: data.map_width,
  mapHeight: data.map_height,
  mapBackground: data.map_background,
  startPointX: data.start_point_x,
  startPointY: data.start_point_y,
  startPointName: data.start_point_name || '',
  isActive: data.is_active,
});

interface WarehouseContextType {
  warehouses: Warehouse[];
  selectedWarehouse: Warehouse | null;
  loading: boolean;
  error: string | null;
  isReady: boolean; // True when warehouses are loaded and selected
  setSelectedWarehouse: (warehouse: Warehouse | null) => void;
  refreshWarehouses: () => Promise<void>;
}

const WarehouseContext = createContext<WarehouseContextType | undefined>(undefined);

interface WarehouseProviderProps {
  children: ReactNode;
}

// Global flag to track if warehouses are ready (for axios interceptor)
let warehousesReady = false;

// Setup axios interceptor to delay API calls until warehouses are ready
// This interceptor runs before the request is sent
axios.interceptors.request.use(
  async (config) => {
    // Allow auth endpoints and warehouses endpoint to pass through immediately
    const url = config.url || '';
    const isAuthEndpoint = url.includes('/auth/');
    const isWarehouseEndpoint = url.includes('/warehouses/');
    
    // If it's not auth or warehouses endpoint, wait until warehouses are ready
    if (!isAuthEndpoint && !isWarehouseEndpoint) {
      // Wait for warehouses to be ready (poll every 100ms, max 10 seconds)
      const maxWaitTime = 10000; // 10 seconds
      const pollInterval = 100; // 100ms
      const startTime = Date.now();
      
      while (!warehousesReady && (Date.now() - startTime) < maxWaitTime) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
      
      // If still not ready after max wait time, proceed anyway (to avoid infinite wait)
      if (!warehousesReady) {
        console.warn('Warehouses not ready after timeout, proceeding with request');
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const WarehouseProvider: React.FC<WarehouseProviderProps> = ({ children }) => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouseState] = useState<Warehouse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  const { API_BASE_URL } = require('../utils/apiConfig');

  // Calculate if warehouses are ready (loaded and selected)
  const isReady = !loading && selectedWarehouse !== null && warehouses.length > 0;

  // Load warehouses from API
  const loadWarehouses = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      // Use axios instead of fetch to benefit from token refresh interceptor
      const response = await axios.get(`${API_BASE_URL}/warehouses/`);
      const responseData = response.data;
      
      // Handle paginated response (DRF may return { results: [...] })
      let rawData: WarehouseApiResponse[];
      if (Array.isArray(responseData)) {
        rawData = responseData;
      } else if (responseData.results && Array.isArray(responseData.results)) {
        rawData = responseData.results;
      } else {
        console.error('Unexpected response format:', responseData);
        throw new Error('Nieprawidłowy format odpowiedzi z serwera');
      }
      
      // Convert API data to frontend format
      const data = rawData.map(mapWarehouseFromApi);
      setWarehouses(data);

      // Restore previously selected warehouse from localStorage
      const savedWarehouseId = localStorage.getItem('selectedWarehouseId');
      if (savedWarehouseId) {
        const savedWarehouse = data.find((w: Warehouse) => w.id === Number(savedWarehouseId));
        if (savedWarehouse) {
          setSelectedWarehouseState(savedWarehouse);
        } else if (data.length > 0) {
          setSelectedWarehouseState(data[0]);
        }
      } else if (data.length > 0) {
        // Select first warehouse by default
        setSelectedWarehouseState(data[0]);
        localStorage.setItem('selectedWarehouseId', String(data[0].id));
      }

      // Update global flag when warehouses are loaded
      warehousesReady = true;
    } catch (err) {
      console.error('Failed to load warehouses:', err);
      
      // Handle axios errors
      if (axios.isAxiosError(err)) {
        const errorMessage = err.response?.data?.detail || 
                            err.response?.data?.error || 
                            err.response?.data?.message ||
                            err.message || 
                            'Nieznany błąd';
        
        let userMessage = 'Nie udało się załadować magazynów';
        
        if (err.response?.status === 401) {
          userMessage = 'Sesja wygasła. Zaloguj się ponownie.';
        } else if (err.response?.status === 403) {
          userMessage = 'Brak uprawnień do wyświetlenia magazynów.';
        } else if (err.response?.status && err.response.status >= 500) {
          userMessage = 'Błąd serwera. Spróbuj ponownie później.';
        } else if (err.code === 'ERR_NETWORK' || err.message.includes('Network Error')) {
          userMessage = 'Błąd połączenia z serwerem. Sprawdź połączenie sieciowe.';
        } else {
          userMessage = `Błąd: ${errorMessage}`;
        }
        
        setError(userMessage);
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Nie udało się załadować magazynów';
        setError(errorMessage);
      }
      
      setWarehouses([]);
      warehousesReady = false;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, API_BASE_URL]);

  // Update global flag when ready state changes
  useEffect(() => {
    warehousesReady = isReady;
  }, [isReady]);

  // Load warehouses when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadWarehouses();
    } else {
      setWarehouses([]);
      setSelectedWarehouseState(null);
      warehousesReady = false;
    }
  }, [isAuthenticated, loadWarehouses]);

  // Set selected warehouse and save to localStorage
  const setSelectedWarehouse = useCallback((warehouse: Warehouse | null) => {
    setSelectedWarehouseState(warehouse);
    if (warehouse) {
      localStorage.setItem('selectedWarehouseId', String(warehouse.id));
    } else {
      localStorage.removeItem('selectedWarehouseId');
    }
  }, []);

  const value: WarehouseContextType = {
    warehouses,
    selectedWarehouse,
    loading,
    error,
    isReady,
    setSelectedWarehouse,
    refreshWarehouses: loadWarehouses,
  };

  return (
    <WarehouseContext.Provider value={value}>
      {children}
    </WarehouseContext.Provider>
  );
};

export const useWarehouse = (): WarehouseContextType => {
  const context = useContext(WarehouseContext);
  if (context === undefined) {
    throw new Error('useWarehouse must be used within a WarehouseProvider');
  }
  return context;
};

export default WarehouseContext;

